import { Modal, Form, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { createApiService } from "./shared/apiConfig";
import {
  MonitorStripeApiApi,
  ServerTransferConnectedAccountDropdown,
} from "./shared/api/axios-client";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { Select } from "./Controls/Select";
import { useNavigate } from "react-router-dom";
import { Copy } from "react-bootstrap-icons";
import { Button } from "./Controls/Button";
import { toast } from "react-toastify";

interface PayoutAccountModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onSave: (data: PayoutAccountForm) => void;
  selectedPayoutAccount: ServerTransferConnectedAccountDropdown | undefined;
  facilityId: number;
  facilityName: string;
  isNewFacility?: boolean;
}

export interface PayoutAccountForm {
  payoutAccountId: string;
  payoutAccountName: string;
}

function ClipboardIcon({ payload, tooltipText, icon }: any) {
  const handleCopy = () => {
    navigator.clipboard.writeText(payload);
  };

  return (
    <OverlayTrigger
      trigger="click"
      placement="top"
      overlay={<Tooltip id="clipboard-tooltip">{tooltipText}</Tooltip>}
      rootClose
    >
      <span role="button" onClick={handleCopy} style={{ cursor: "pointer" }}>
        {icon}
      </span>
    </OverlayTrigger>
  );
}

export function PayoutAccountModal({
  show,
  setShow,
  onSave,
  selectedPayoutAccount,
  facilityId,
  isNewFacility,
  facilityName,
}: PayoutAccountModalProps) {
  const { register, watch, handleSubmit, setValue, reset } =
    useForm<PayoutAccountForm>({
      defaultValues: {
        payoutAccountId: selectedPayoutAccount?.id ?? "",
        payoutAccountName: selectedPayoutAccount?.label ?? "",
      },
    });

  const [connectedAccounts, setConnectedAccounts] = useState<
    ServerTransferConnectedAccountDropdown[]
  >([]);
  const [shareLink, setShareLink] = useState<string>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [changed, setChanged] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const nav = useNavigate();

  const stripeApi = createApiService(MonitorStripeApiApi);
  const payoutAccount = watch("payoutAccountId");

  const selectedAccount = connectedAccounts.find(
    ( acc ) => acc.id === payoutAccount
  );

  async function loadShareLink(
    accountId?: string,
    accounts: ServerTransferConnectedAccountDropdown[] = connectedAccounts,
  ) {
    const processorAccountKey = accountId ?? payoutAccount;
    if ( !processorAccountKey ) {
      setShareLink( undefined );
      return;
    }

    const account = accounts.find( ( acc ) => acc.id === processorAccountKey );
    if ( account?.isAccountOnboarded ) {
      setShareLink( undefined );
      return;
    }

    setShareLoading( true );
    setShareLink( undefined );
    try {
      const res = await stripeApi.monitorStripePayoutOnboardingShare( {
        facilityId,
        serverTransferPayoutOnboardingShareReq: {
          processorAccountKey,
        },
      } );
      setShareLink( res.data.shareLink! );
    } catch {
      toast.error( "Failed to create share link. Please try again." );
      setShareLink( undefined );
    } finally {
      setShareLoading( false );
    }
  }

  useEffect( () => {
    if ( !show ) return;

    stripeApi.monitorStripeAccounts().then( ( res ) => {
      setConnectedAccounts( res.data );
      if ( !isNewFacility ) {
        const currentId = selectedPayoutAccount?.id ?? payoutAccount;
        if ( currentId ) {
          loadShareLink( currentId, res.data );
        }
      }
    } );
  }, [show, facilityId] );

  async function submit( data: PayoutAccountForm ) {
    if ( !selectedAccount ) {
      const res = await stripeApi.monitorStripeCreateNewAccount( {
        facilityName,
        facilityId,
      } );
      const newAccount = {
        id: res.data.id!,
        label: res.data.label,
      };
      setConnectedAccounts( ( prev ) => [...prev, newAccount] );
      setValue( "payoutAccountId", newAccount.id, {
        shouldValidate: true,
      } );
      setValue( "payoutAccountName", newAccount.label!, {
        shouldValidate: true,
      } );

      data.payoutAccountId = newAccount.id;
      data.payoutAccountName = newAccount.label!;
      onSave( data );
      setShareLink( undefined );
      setChanged( false );
      return;
    }

    onSave( data );
    setShow( false );
    if ( isNewFacility ) nav( "/operator/facilities" );
    setChanged( false );
  }

  function onClose() {
    reset();
    setShow( false );
    setChanged( false );
    setShareLink( undefined );
    if ( isNewFacility ) nav( "/operator/facilities" );
  }

  return (
    <Modal show={show} centered onHide={() => onClose()}>
      <Form
        onSubmit={handleSubmit( ( data ) => {
          setPendingData( data );
          if ( !selectedAccount ) {
            submit( data );
            return;
          }
          setShowConfirm( true );
        } )}
      >
        <Modal.Header>Edit Payout Account</Modal.Header>
        <Modal.Body>
          <Select
            label="Payout Account"
            value={payoutAccount}
            {...register( "payoutAccountId", {
              onChange: ( e ) => {
                const accountId = e.target.value;
                const account = connectedAccounts.find(
                  ( x ) => x.id === accountId
                );
                setValue( "payoutAccountId", accountId, {
                  shouldValidate: false,
                } );
                if ( account ) {
                  setValue( "payoutAccountName", account.label!, {
                    shouldValidate: false,
                  } );
                }
                setShareLink( undefined );
                setChanged( true );
                if ( accountId && account && !account.isAccountOnboarded ) {
                  loadShareLink( accountId, connectedAccounts );
                }
              },
            } )}
          >
            {connectedAccounts.map( ( acc ) => (
              <option key={acc.id} value={acc.id!}>
                {acc.label}
              </option>
            ) )}
            <option value="">➕ Create New</option>
          </Select>

          {selectedAccount && !selectedAccount.isAccountOnboarded && (
            <Alert variant="info" className="mt-3 mb-0">
              {shareLoading && !shareLink && (
                <p className="mb-0 small text-muted">Generating link…</p>
              )}
              {!shareLoading && !shareLink && (
                <p className="mb-0 small text-muted">
                  Click Generate onboarding link to create a shareable link.
                </p>
              )}
              {shareLink && (
                <>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={shareLink}
                      readOnly
                      onFocus={( e ) => e.target.select()}
                    />
                    <span className="input-group-text">
                      <ClipboardIcon
                        payload={shareLink}
                        tooltipText="Link copied!"
                        icon={<Copy className="small" />}
                      />
                    </span>
                  </div>
                  <div className="mt-3 text-muted small">
                    Copy this link and send it to anyone who should complete
                    Stripe payout setup. The link expires in 7 days.
                  </div>
                </>
              )}
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <div>
            <Button onClick={() => onClose()}>Close</Button>
            <Button
              type="submit"
              className="btn-main ms-2"
              disabled={!changed && !!selectedAccount}
            >
              Save
            </Button>
          </div>
          {selectedAccount && !selectedAccount.isAccountOnboarded && (
            <Button
              variant="outline-primary"
              disabled={shareLoading}
              onClick={async () => {
                const res = await stripeApi.monitorStripeAccounts();
                setConnectedAccounts( res.data );
                await loadShareLink( payoutAccount, res.data );
                toast.success( "Link refreshed!" );
              }}
            >
              {shareLink ? "Refresh" : "Generate onboarding link"}
            </Button>
          )}
        </Modal.Footer>
      </Form>

      <ConfirmModal
        title="Change Payout Account"
        message="Are you sure you want to change the account? This will affect all future payments."
        show={showConfirm}
        setShow={setShowConfirm}
        onConfirm={() => {
          if ( pendingData ) {
            submit( pendingData );
            setPendingData( null );
            setShow( false );
          }
        }}
      />
    </Modal>
  );
}
